import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, setAuthToken, setSessionExpiredHandler } from "./api";

const AuthContext = createContext(null);
const SESSION_STARTED_KEY = "sh_session_started_at";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const sessionExpiredShownRef = useRef(false);

  const mergeExtended = async (baseUser) => {
    if (!baseUser) return baseUser;
    try {
      const { data } = await api.get("/system/me");
      const perms = Array.from(new Set([...(baseUser.permissions || []), ...(data.permissions || [])]));
      return { ...baseUser, permissions: perms };
    } catch (e) {
      return baseUser;
    }
  };

  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (sessionExpiredShownRef.current) return;
      sessionExpiredShownRef.current = true;
      setUser(false);
      toast.error("A sua sessao expirou. Inicie sessao novamente.", { duration: 6000 });
      setTimeout(() => { sessionExpiredShownRef.current = false; }, 10000);
    });
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(async (r) => {
        // Sessao ja existente (ex: refresh da pagina) sem marca de inicio guardada - usa agora como fallback.
        if (!localStorage.getItem(SESSION_STARTED_KEY)) {
          localStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
        }
        const u = await mergeExtended(r.data);
        setUser(u);
      })
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // MFA em 2 passos
    if (data.mfa_required) {
      return { mfa_required: true, require_mfa_enroll: !!data.require_mfa_enroll,
                telegram_available: !!data.telegram_available,
                preauth_token: data.preauth_token };
    }
    if (data.access_token) setAuthToken(data.access_token);
    localStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
    const merged = await mergeExtended(data);
    setUser(merged);
    return merged;
  };

  const completeMfa = async (preauth_token, code) => {
    const { data } = await api.post("/auth/mfa/complete", { preauth_token, code });
    if (data.access_token) setAuthToken(data.access_token);
    localStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
    const merged = await mergeExtended(data);
    setUser(merged);
    return merged;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setAuthToken(null);
    localStorage.removeItem(SESSION_STARTED_KEY);
    setUser(false);
  };

  const can = (perm) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes("*") || user.permissions.includes(perm);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, completeMfa, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
